import type { Role } from "@hopitech/contracts";

/** Charte par édition (ADR-0007) : chaque salon porte son accent. */
export interface Theme {
  accent: string;
  soft: string;
}
export interface Edition {
  id: number;
  optionLabel: string;
  ville: string;
  dates: string;
  theme: Theme;
}

// Identifiants d'édition = event_id du système hérité (le 10 n'existe pas). Trois chartes en rotation.
const T0 = { accent: "#2C3178", soft: "#E7E6F2" };
const T1 = { accent: "#245C3C", soft: "#E4F1E8" };
const T2 = { accent: "#8E2C23", soft: "#FBE6E3" };
export const EDITIONS: readonly Edition[] = [
  { id: 12, optionLabel: "Hopitech 2027", ville: "Arles", dates: "24–26 mars 2027", theme: T0 },
  { id: 11, optionLabel: "Hopitech 2026", ville: "Le Touquet-Paris-Plage", dates: "25–27 mars 2026", theme: T1 },
  { id: 9, optionLabel: "Hopitech 2025", ville: "La Rochelle", dates: "26–28 mars 2025", theme: T2 },
  { id: 8, optionLabel: "Hopitech 2024", ville: "Beaune", dates: "27–29 mars 2024", theme: T0 },
  { id: 7, optionLabel: "Hopitech 2023", ville: "Toulouse", dates: "Mars 2023", theme: T1 },
  { id: 6, optionLabel: "Hopitech 2021", ville: "—", dates: "2021", theme: T2 },
  { id: 5, optionLabel: "Hopitech 2020", ville: "Caen", dates: "Mars 2020", theme: T0 },
  { id: 4, optionLabel: "Hopitech 2019", ville: "Poitiers", dates: "Mars 2019", theme: T1 },
  { id: 3, optionLabel: "Hopitech 2018", ville: "Paris", dates: "Mars 2018", theme: T2 },
  { id: 2, optionLabel: "Hopitech 2017", ville: "Poitiers", dates: "Mars 2017", theme: T0 },
  { id: 1, optionLabel: "Hopitech 2016", ville: "Saint-Étienne", dates: "Mars 2016", theme: T1 },
];

export interface RoleUI {
  id: Role;
  label: string;
  accueil: string;
}
export const ROLES_UI: readonly RoleUI[] = [
  { id: "exposant", label: "Exposant", accueil: "/exposant" },
  { id: "stagiaire", label: "Stagiaire", accueil: "/stagiaire" },
  { id: "intervenant", label: "Intervenant", accueil: "/intervenant" },
  { id: "conferencier", label: "Conférencier", accueil: "/conferencier" },
  { id: "organisateur", label: "Organisateur", accueil: "/organisateur" },
  { id: "invite", label: "Invité", accueil: "/invite" },
  { id: "visiteur", label: "Visiteur", accueil: "/visiteur" },
  { id: "gestionnaire_visiteurs", label: "Gestionnaire visiteurs", accueil: "/gestionnaire" },
  { id: "administrateur", label: "Administrateur", accueil: "/admin" },
];

export function accueilRole(role: Role): string {
  return ROLES_UI.find((r) => r.id === role)?.accueil ?? "/";
}

export interface NavItem {
  ref: string;
  label: string;
  route: string;
}
export interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

export function navPour(role: Role): readonly NavGroup[] {
  switch (role) {
    case "exposant":
      return [
        { label: "Mon salon", items: [
          { ref: "B1", label: "Tableau de bord", route: "/exposant" },
          { ref: "A2", label: "Ma réservation", route: "/exposant/reservation" },
          { ref: "B1", label: "Mes documents", route: "/exposant/documents" },
        ] },
        { label: "Sur le salon", items: [
          { ref: "C1", label: "Mes prospects", route: "/exposant/prospects" },
          { ref: "C2", label: "Gamification", route: "/exposant/gamification" },
        ] },
        { label: "Mon équipe", items: [
          { ref: "B1", label: "Mes collaborateurs", route: "/exposant/collaborateurs" },
          { ref: "A5", label: "Mes invités", route: "/exposant/invites" },
        ] },
      ];
    case "administrateur":
      return [
        { label: "Pilotage", items: [{ ref: "D0", label: "Tableau de bord", route: "/admin" }] },
        { label: "Édition", items: [
          { ref: "D1", label: "Salons", route: "/admin/salons" },
          { ref: "D2", label: "Fiche édition", route: "/admin/salon" },
          { ref: "D6", label: "Ateliers", route: "/admin/ateliers" },
          { ref: "D11", label: "Inscriptions compl.", route: "/admin/inscriptions" },
          { ref: "D12", label: "Catalogue · Sellsy", route: "/admin/catalogue" },
        ] },
        { label: "Participants", items: [
          { ref: "D3", label: "Exposants", route: "/admin/exposants" },
          { ref: "D3", label: "Plan des emplacements", route: "/admin/plan" },
          { ref: "D4", label: "Stagiaires", route: "/admin/stagiaires" },
          { ref: "D4", label: "Présences", route: "/admin/presences" },
          { ref: "D7", label: "Intervenants", route: "/admin/intervenants" },
          { ref: "D8", label: "Organisateurs", route: "/admin/organisateurs" },
          { ref: "D9", label: "Visiteurs", route: "/admin/visiteurs" },
          { ref: "D10", label: "Invités", route: "/admin/invites" },
        ] },
        { label: "Référentiels & outils", items: [
          { ref: "C3", label: "Accueil sur place", route: "/admin/accueil" },
          { ref: "D13", label: "Documents & exports", route: "/admin/documents" },
          { ref: "D14", label: "Gamification", route: "/admin/gamification" },
          { ref: "D15", label: "Établissements", route: "/admin/etablissements" },
          { ref: "D16", label: "Comptes & rôles", route: "/admin/comptes" },
          { ref: "D16", label: "Journal", route: "/admin/journal" },
        ] },
      ];
    case "stagiaire":
      return [
        { label: "Ma formation", items: [
          { ref: "B2", label: "Tableau de bord", route: "/stagiaire" },
          { ref: "A3", label: "Mon inscription", route: "/inscription/stagiaire" },
          { ref: "B2", label: "Mon planning", route: "/stagiaire/planning" },
          { ref: "B2", label: "Mes documents", route: "/stagiaire/documents" },
        ] },
        { label: "Sur le salon", items: [{ ref: "C2", label: "Gamification", route: "/stagiaire/jeu" }] },
      ];
    case "visiteur":
    case "invite":
      return [
        { label: "Ma visite", items: [
          { ref: "B6", label: "Mon badge", route: "/visiteur" },
          { ref: "A4", label: "Mon inscription", route: "/inscription/visiteur" },
          { ref: "C1", label: "Mon parcours", route: "/visiteur/parcours" },
          { ref: "C2", label: "Gamification", route: "/visiteur/jeu" },
        ] },
      ];
    default:
      return [
        { label: "Mon espace", items: [
          { ref: "B3", label: "Tableau de bord", route: accueilRole(role) },
          { ref: "B3", label: "Mon planning", route: `${accueilRole(role)}/planning` },
          { ref: "B0", label: "Mes documents", route: `${accueilRole(role)}/documents` },
        ] },
      ];
  }
}

export interface ScreenMeta {
  ref: string;
  label: string;
  sub: string;
}

const SOUS_TITRES: Record<string, string> = {
  "/exposant": "Votre présence sur l'édition en cours, en un écran.",
  "/exposant/reservation": "Composez votre stand et vos options, puis validez.",
  "/exposant/prospects": "Les contacts scannés sur le stand, classés par date et heure.",
  "/exposant/gamification": "Le jeu de l'édition.",
  "/exposant/documents": "Vos badges et pièces de l'édition.",
  "/exposant/invites": "Invitez des visiteurs et suivez qui s'est inscrit.",
  "/exposant/collaborateurs": "L'équipe présente sur le salon — chacun reçoit un accès.",
  "/stagiaire": "Inscription, planning, documents et jeu de l'édition.",
  "/visiteur": "Votre badge, votre parcours et le jeu.",
  "/inscription/stagiaire": "Vous, ou vos collaborateurs — établissement dans le référentiel.",
  "/inscription/visiteur": "Préinscription — le badge se retire à l'accueil.",
  "/admin/accueil": "Retrait de badge sur place, préinscrits et inscription minute.",
  "/admin": "Vue d'ensemble d'une édition : fréquentation, commercialisation, fidélité.",
  "/admin/salons": "11 éditions, de 2016 à 2027.",
  "/admin/salon": "Informations, calendrier d'ouverture, charte, gabarits de documents.",
  "/admin/ateliers": "Créer et ordonner les ateliers de l'édition.",
  "/admin/inscriptions": "Créer des inscriptions hors parcours standard.",
  "/admin/catalogue": "La vérité tarifaire vient de Sellsy. Maquetté en lecture.",
  "/admin/exposants": "Gérer les exposants et attribuer les emplacements.",
  "/admin/plan": "Rapprocher un souhait de placement d'un emplacement réel.",
  "/admin/stagiaires": "Liste filtrable, export CSV, commande de formation, badges.",
  "/admin/presences": "Import des logs « douchettes » et rapprochement des émargements.",
  "/admin/intervenants": "Liste, badges, fiche = formulaire d'inscription intervenant.",
  "/admin/organisateurs": "Liste, badges, fiche = formulaire d'inscription organisateur.",
  "/admin/visiteurs": "Visiteur Salon ou Invité Exposant — l'invité porte un indicateur.",
  "/admin/invites": "Vue filtrée des visiteurs, par exposant invitant.",
  "/admin/documents": "Produire les pièces en masse et les extractions, tracées par rôle.",
  "/admin/gamification": "Questions, réponses, association aux exposants, règles de points.",
  "/admin/etablissements": "Référentiel national alimenté par une source publique.",
  "/admin/comptes": "Rôle par édition, jamais de mot de passe affiché, émission d'un lien d'accès.",
  "/admin/journal": "Connexions, émissions, reprises manuelles, révocations — append-only.",
};

/** Construit la table des écrans à partir de toutes les navigations, une fois. */
export const SCREENS: Record<string, ScreenMeta> = (() => {
  const table: Record<string, ScreenMeta> = {};
  for (const r of ROLES_UI) {
    for (const g of navPour(r.id)) {
      for (const it of g.items) {
        if (!table[it.route]) {
          table[it.route] = { ref: it.ref, label: it.label, sub: SOUS_TITRES[it.route] ?? g.label };
        }
      }
    }
  }
  return table;
})();

export function metaPour(chemin: string): ScreenMeta {
  return SCREENS[chemin] ?? { ref: "—", label: "Hopitech", sub: "" };
}
